import { useEffect, useLayoutEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Navigation-aware scroll manager.
 *
 * Forward navigation (PUSH/REPLACE — tapping into a page) scrolls to the
 * top like before: React Router preserves scroll by default, which lands
 * long pages (Barrel Detail, Catalog) halfway down when you click in
 * from a list.
 *
 * Back/forward navigation (POP) restores the position the page was at
 * when you left it — so Catalog → barrel → back returns you to the same
 * spot in the list instead of the top. Positions are remembered per
 * history entry (location.key) in-memory; a reload starts fresh, which
 * matches browser behavior for SPA scroll state.
 *
 * Restoring is retried over a few frames because page content arrives
 * async (live stores, windowed lists) — we scroll as soon as the page is
 * tall enough for the target, giving up after ~1s.
 *
 * Mount once under <Router>.
 */

const positions = new Map<string, number>();

export default function ScrollToTop() {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Record the scroll position of the page we're LEAVING. The cleanup
  // for history entry A runs right as the location flips to B — exactly
  // the moment A's position is final.
  useEffect(() => {
    const key = location.key;
    return () => {
      positions.set(key, window.scrollY);
    };
  }, [location.key]);

  useLayoutEffect(() => {
    if (navigationType === 'POP') {
      const target = positions.get(location.key) ?? 0;
      if (target <= 0) {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        return;
      }
      // Retry until the document is tall enough to reach the target
      // (content loads async), then snap there. Give up after ~1s.
      let cancelled = false;
      const startedAt = performance.now();
      const attempt = () => {
        if (cancelled) return;
        const maxScroll =
          document.documentElement.scrollHeight - window.innerHeight;
        if (maxScroll >= target || performance.now() - startedAt > 1000) {
          window.scrollTo({ top: Math.min(target, Math.max(0, maxScroll)), left: 0, behavior: 'auto' });
          return;
        }
        requestAnimationFrame(attempt);
      };
      attempt();
      return () => {
        cancelled = true;
      };
    }
    // PUSH / REPLACE — fresh page, start at the top.
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.key, navigationType]);

  return null;
}
